import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { settleFirstOrderGift } from '@theme/first-order-gift';
import {
  findSellingPlanIdByFrequencyLabel,
  getCartSubscriptionFrequencyLabel,
  restoreGroupFrequencySelect,
  setCartSubscriptionFrequencyLabel,
} from '@theme/utilities';

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.openCartIcons = null;

    this.drawerClickHandler = this.#onDrawerClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
    this.pageShowHandler = this.#onPageShow.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.drawerClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);
    window.addEventListener('pageshow', this.pageShowHandler);

    this.#resetCheckoutButtonState();

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.removeEventListener('click', this.drawerClickHandler);
    window.removeEventListener('pageshow', this.pageShowHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #onPageShow() {
    this.#resetCheckoutButtonState();
  }

  #resetCheckoutButtonState() {
    for (const checkoutButton of this.querySelectorAll('[data-ref="checkout-button"]')) {
      checkoutButton.classList.remove('is-loading');
    }
  }

  #onDrawerClick(event) {
    const checkoutButton = event.target.closest('[data-ref="checkout-button"]');
    if (checkoutButton && this.contains(checkoutButton)) {
      checkoutButton.classList.add('is-loading');
    }

    this.#onBackdropClick(event);
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);

    const source = event.detail?.data?.source;
    const shouldSyncSubscriptionFrequency = ['product-form', 'cross-sell', 'cart-drawer-item'].includes(source);

    queueMicrotask(() => {
      const group = this.querySelector('cart-drawer-subscription-group');
      group?.restoreSavedFrequency?.();

      if (shouldSyncSubscriptionFrequency) {
        group?.syncToGroupFrequency?.();
      }
    });
  }

  #onBackdropClick(event) {
    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    this.#resetCheckoutButtonState();

    // Nettoyer les classes pour éviter les conflits
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    // Ajouter la classe de fermeture pour déclencher l'animation
    this.classList.add('is-closing');

    // Attendre la fin de l'animation avant de masquer complètement
    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125); // Durée légèrement inférieure à --animation-speed pour éviter les décalages
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  renderCartContents(parsedState) {
    const section_cart_drawer_id = this.dataset.sectionId;
    const nextMarkup = parsedState.sections?.[section_cart_drawer_id];
    if (!nextMarkup) return;

    const currentCartDrawer = this.querySelector('.cart-drawer__inner');
    const newCartDrawer = new DOMParser().parseFromString(nextMarkup, 'text/html');

    currentCartDrawer.innerHTML = newCartDrawer.querySelector('.cart-drawer__inner').innerHTML;

    setTimeout(() => {
      if (parsedState.itemCount == 0) {
        this.classList.add('cart-drawer--empty');
        setCartSubscriptionFrequencyLabel(null);
      } else {
        this.classList.remove('cart-drawer--empty');
      }
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerSubscriptionGroup extends HTMLElement {
  constructor() {
    super();

    this.frequencySelect = null;
    this.frequencyChangeHandler = this.#handleFrequencySelectChange.bind(this);
  }

  connectedCallback() {
    this.frequencySelect = this.querySelector('[data-ref="subscription-group-frequency-select"]');

    this.frequencySelect?.addEventListener('change', this.frequencyChangeHandler);
    this.restoreSavedFrequency();
  }

  disconnectedCallback() {
    this.frequencySelect?.removeEventListener('change', this.frequencyChangeHandler);
  }

  restoreSavedFrequency() {
    if (!(this.frequencySelect instanceof HTMLSelectElement)) return;

    restoreGroupFrequencySelect(this.frequencySelect);
    this.#updateFrequencySelectDisplay();
  }

  #handleFrequencySelectChange(event) {
    event.preventDefault();
    this.#persistSelectedFrequencyLabel();
    this.#updateFrequencySelectDisplay();
    this.#updateSubscriptionGroup();
  }

  #persistSelectedFrequencyLabel() {
    const select = this.frequencySelect;
    if (!(select instanceof HTMLSelectElement)) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption?.value) {
      setCartSubscriptionFrequencyLabel(null);
      return;
    }

    const label = selectedOption.dataset.labelFull || selectedOption.textContent?.trim() || '';
    setCartSubscriptionFrequencyLabel(label || null);
  }

  #updateFrequencySelectDisplay() {
    const select = this.frequencySelect;
    const display = this.querySelector('[data-ref="subscription-group-frequency-display"]');
    if (!select || !display) return;

    const option = select.options[select.selectedIndex];
    if (!option) return;

    const label = option.dataset.labelFull || option.textContent?.trim() || '';
    display.textContent = label;

    const prefix = select.dataset.labelPrefix || '';
    select.setAttribute('aria-label', label && prefix ? `${prefix}: ${label}` : label || prefix);
  }

  syncToGroupFrequency() {
    this.restoreSavedFrequency();

    const select = this.frequencySelect;
    if (!select?.value) return;

    const selectedLabel = getCartSubscriptionFrequencyLabel();
    if (!selectedLabel) return;

    const subscribedItems = Array.from(this.querySelectorAll('cart-drawer-item')).filter((item) =>
      item.querySelector('[data-ref="frequency-select"]'),
    );
    if (subscribedItems.length === 0) return;

    const needsSync = subscribedItems.some((item) => {
      const itemSelect = item.querySelector('[data-ref="frequency-select"]');
      if (!(itemSelect instanceof HTMLSelectElement)) return false;

      const itemOption = itemSelect.options[itemSelect.selectedIndex];
      if (!itemOption) return false;

      const itemLabel = itemOption.dataset.labelFull || itemOption.textContent?.trim() || '';
      return itemLabel !== selectedLabel;
    });

    if (!needsSync) return;

    this.#updateSubscriptionGroup();
  }

  async #updateSubscriptionGroup() {
    const select = this.frequencySelect;
    const cartDrawer = this.closest('cart-drawer');
    if (!select || !cartDrawer) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    const selectedLabel = selectedOption.dataset.labelFull || selectedOption.textContent?.trim() || '';
    setCartSubscriptionFrequencyLabel(selectedLabel || null);
    const subscribedItems = Array.from(this.querySelectorAll('cart-drawer-item')).filter((item) =>
      item.querySelector('[data-ref="frequency-select"]'),
    );
    if (subscribedItems.length === 0) return;

    this.classList.add('cart-drawer__group--loading');

    try {
      let lastParsedResponseText = null;

      for (const item of subscribedItems) {
        const lineKey = item.dataset.lineKey;
        const itemSelect = item.querySelector('[data-ref="frequency-select"]');
        if (!lineKey || !itemSelect) continue;

        const matchingOption = Array.from(itemSelect.options).find((option) => {
          const optionLabel = option.dataset.labelFull || option.textContent?.trim() || '';
          return optionLabel === selectedLabel;
        });
        if (!matchingOption) continue;

        const sellingPlanId = matchingOption.value ?? '';
        const body = JSON.stringify({
          id: lineKey,
          selling_plan: sellingPlanId === '' ? null : Number(sellingPlanId),
          sections: cartDrawer.dataset.sectionId,
          sections_url: window.location.pathname,
        });

        const response = await fetch(Theme.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
        });

        lastParsedResponseText = JSON.parse(await response.text());

        if (lastParsedResponseText.errors) {
          const message = lastParsedResponseText.errors.join(', ');
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message } }));
          return;
        }
      }

      if (!lastParsedResponseText) return;

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: lastParsedResponseText.item_count,
            source: 'cart-drawer-subscription-group',
            sections: lastParsedResponseText.sections,
          },
        ),
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.classList.remove('cart-drawer__group--loading');
    }
  }
}

if (!customElements.get('cart-drawer-subscription-group')) {
  customElements.define('cart-drawer-subscription-group', CartDrawerSubscriptionGroup);
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.itemClickHandler = this.#handleClick.bind(this);
    this.itemChangeHandler = this.#handleChange.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this.itemClickHandler);
    this.addEventListener('change', this.itemChangeHandler);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.itemClickHandler);
    this.removeEventListener('change', this.itemChangeHandler);
  }

  #handleClick(event) {
    const removeButton = event.target.closest('[data-ref="remove-item"]');
    if (removeButton && this.contains(removeButton)) {
      event.preventDefault();
      this.#updateLineItemQuantity(0);
      return;
    }

    const subscriptionButton = event.target.closest('[data-ref="add-subscription"]');
    if (!subscriptionButton || !this.contains(subscriptionButton)) return;

    event.preventDefault();
    this.#updateLineItemSubscription(this.#resolveSellingPlanIdForCart(subscriptionButton.dataset.sellingPlanId));
  }

  #handleChange(event) {
    const quantitySelectorInput = event.target.closest('[data-ref="quantity-selector-input"]');
    if (quantitySelectorInput && this.contains(quantitySelectorInput)) {
      event.preventDefault();
      this.#updateLineItemQuantity(quantitySelectorInput.value);
      return;
    }

    const frequencySelect = event.target.closest('[data-ref="frequency-select"]');
    if (!frequencySelect || !this.contains(frequencySelect)) return;

    event.preventDefault();
    this.#updateLineItemSubscription(frequencySelect.value ?? '');
  }

  #resolveSellingPlanIdForCart(preferredPlanId) {
    if (!preferredPlanId) return preferredPlanId;

    const cartLabel = getCartSubscriptionFrequencyLabel();
    if (!cartLabel) return preferredPlanId;

    const matchedPlanId = findSellingPlanIdByFrequencyLabel(this, cartLabel);
    return matchedPlanId || preferredPlanId;
  }

  #updateLineItemSubscription(sellingPlanId) {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    this.#enableLoading();

    const plan =
      sellingPlanId === '' || sellingPlanId === null || sellingPlanId === undefined
        ? null
        : Number(sellingPlanId);

    const body = JSON.stringify({
      id: lineKey,
      selling_plan: plan,
      sections: this.closest('cart-drawer').dataset.sectionId,
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/json` },
      ...{ body },
    })
      .then((response) => {
        return response.text();
      })
      .then((responsetext) => {
        const parsedResponseText = JSON.parse(responsetext);

        if (parsedResponseText.errors) {
          const message = parsedResponseText.errors.join(', ');
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: message } }));
          return;
        }

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: parsedResponseText.item_count,
              source: 'cart-drawer-item',
              sections: parsedResponseText.sections,
            },
          ),
        );
      })
      .catch((e) => {
        console.error(e);
        this.#disableLoading();
      })
      .finally(() => {
        this.#disableLoading();
      });
  }

  #updateLineItemQuantity(quantity) {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    this.#enableLoading();

    const body = JSON.stringify({
      id: lineKey,
      quantity: Number(quantity),
      sections: this.closest('cart-drawer').dataset.sectionId,
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/json` },
      ...{ body },
    })
      .then((response) => {
        return response.text();
      })
      .then(async (responsetext) => {
        let cart = JSON.parse(responsetext);

        if (cart.errors) {
          const message = cart.errors.join(', ');
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: message } }));
          return;
        }

        cart = await settleFirstOrderGift(cart, (lineKey, lineQuantity) => this.#changeLine(lineKey, lineQuantity));

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: cart.item_count,
              source: 'cart-drawer-item',
              sections: cart.sections,
            },
          ),
        );
      })
      .catch((e) => {
        console.error(e);
        this.#disableLoading();
      })
      .finally(() => {
        this.#disableLoading();
      });
  }

  async #changeLine(lineKey, quantity) {
    const cartDrawer = this.closest('cart-drawer');
    const response = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity,
        sections: cartDrawer?.dataset.sectionId,
        sections_url: window.location.pathname,
      }),
    });
    const cart = JSON.parse(await response.text());
    if (cart.errors) return null;
    return cart;
  }

  #enableLoading() {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    this.classList.add('cart-drawer__item--loading');

    const cartDrawer = this.closest('cart-drawer');
    cartDrawer?.querySelectorAll(`[data-line-key="${lineKey}"]`).forEach((lineHost) => {
      lineHost.classList.add('cart-drawer__item--loading');
    });
  }

  #disableLoading() {
    const lineKey = this.dataset.lineKey;
    if (!lineKey) return;

    this.classList.remove('cart-drawer__item--loading');

    const cartDrawer = this.closest('cart-drawer');
    cartDrawer?.querySelectorAll(`[data-line-key="${lineKey}"]`).forEach((lineHost) => {
      lineHost.classList.remove('cart-drawer__item--loading');
    });
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
